import { toast } from 'react-toastify';
import { callSupabaseConnect } from '~/lib/.client/api/supabase';
import { forkProject } from '~/lib/projects/forkProject';
import { applicationManager } from '~/lib/stores/applicationManager';
import { projectStore } from '~/lib/stores/project';
import { supabaseStore } from '~/lib/stores/supabase';
import type { SupabaseRegion } from '~/types/supabase';
import { NOOP } from '~/utils/noops';
import { wait } from '~/utils/promises';

export async function connectToSupabase(
  forceCreation = false,
  poll = false,
  supabaseProjectData?: { supabaseProjectId: string } | { name: string; password: string; region: SupabaseRegion },
) {
  if (
    supabaseStore.supabaseStatus.get() === 'CONFIGURED' ||
    (supabaseStore.supabaseStatus.get() === 'CONNECTING' && !poll)
  ) {
    return;
  }

  supabaseStore.requiresConfirmation.set('pending');
  supabaseStore.supabaseStatus.set('CONNECTING');

  /**
   * Framework check needs to happen after setting initial status
   * to maintain expected state transitions and dependencies.
   */
  if (!supabaseStore.supported.get()) {
    supabaseStore.supabaseStatus.set('UNSUPPORTED');

    return;
  }

  if (projectStore.shouldFork()) {
    await forkProject();
  }

  try {
    const response = await callSupabaseConnect({
      forceCreation,
      supabaseProjectData,
      pollProjectStatus: () => {
        supabaseStore.requiresConfirmation.set('idle');

        wait(2500).then(() => {
          return connectToSupabase(forceCreation, true, supabaseProjectData);
        });
      },
      supabaseProjectRemoved: () => {
        supabaseStore.requiresConfirmation.set('idle');
        supabaseStore.supabaseStatus.set('NOT_CONFIGURED');
      },
      projectLimitReached: () => {
        supabaseStore.requiresConfirmation.set('idle');
        supabaseStore.supabaseStatus.set('NOT_CONFIGURED');
        supabaseStore.showProjectLimitReached.set(true);
      },
      projectNameAlreadyExists: () => {
        supabaseStore.requiresConfirmation.set('idle');
        supabaseStore.supabaseStatus.set('NOT_CONFIGURED');
        supabaseStore.showProjectNameAlreadyExists.set(true);
      },
      needsExtraConfirmation: () => {
        supabaseStore.supabaseStatus.set('NEED_CONFIRMATION');

        // retry when confirmation has been provided
        const unsubscribe = supabaseStore.requiresConfirmation.subscribe((requireConfirmation) => {
          if (requireConfirmation === 'pending') {
            return;
          }

          unsubscribe();

          // user accepted, retry
          if (requireConfirmation === 'accepted') {
            connectToSupabase(true, false, supabaseProjectData);
          }
        });
      },
      supabaseAuthenticationNeeded: async () => {
        supabaseStore.requiresConfirmation.set('idle');

        reconnectSupabase(() => {
          connectToSupabase(forceCreation, true, supabaseProjectData);
        });
      },
    });

    // call was not successfull but has been handled already
    if (!response) {
      return;
    }

    supabaseStore.requiresConfirmation.set('idle');
    supabaseStore.supabaseProjectIntegration.set(response);
    supabaseStore.supabaseStatus.set('CONFIGURED');
  } catch (error) {
    console.error(error);

    toast.error('Something went wrong while setting up Supabase');

    supabaseStore.supabaseStatus.set('NOT_CONFIGURED');
  }
}

export async function reconnectSupabase(onSuccess: () => void) {
  let unsubscribe = NOOP;

  // only time out after 2 minutes because the oauth flow might take longer when people still need to sign up
  applicationManager.connect('SUPABASE', { timeout: 120_000 });

  try {
    await new Promise<void>((resolve, reject) => {
      unsubscribe = applicationManager.applications.subscribe((newValue, _, changedKey) => {
        if (changedKey !== 'SUPABASE') {
          return;
        }

        if (newValue.SUPABASE === 'CONNECTED') {
          resolve();
        } else if (newValue.SUPABASE === 'DISCONNECTED') {
          reject();
        }
      });
    });

    onSuccess();
  } catch {
    supabaseStore.supabaseStatus.set('NOT_CONFIGURED');
  }

  unsubscribe();
}
